import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import {
  formatTransformValue,
  type AxisTriplet,
} from '../../../lib/skeleton/boneTransforms';
import {
  AXES,
  INSPECTOR_CONTROL_TEXT_STYLE,
  INSPECTOR_DRAG_FAST_MODIFIER,
  INSPECTOR_DRAG_SLOW_MODIFIER,
} from '../constants';
import {
  axisValueDiffersFromBaseline,
} from './axisTripletFieldUtils';

interface AxisTripletFieldsProps {
  values: AxisTriplet;
  editable?: boolean;
  ariaLabelPrefix: string;
  onAxisChange?: (axisIndex: number, nextValue: number) => void;
  baselineValues?: AxisTriplet | null;
  changeEpsilon?: number;
  dragDeltaPerPixel?: number;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}

interface AxisFieldProps {
  axis: (typeof AXES)[number];
  value: number;
  editable: boolean;
  ariaLabel: string;
  changed: boolean;
  onValueChange?: (nextValue: number) => void;
  dragDeltaPerPixel?: number;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}

const AXIS_FIELD_STRIP_CLASS: Record<(typeof AXES)[number], string> = {
  x: 'bg-red-50/65',
  y: 'bg-green-50/65',
  z: 'bg-blue-50/65',
};
const AXIS_FIELD_WRAPPER_BASE_CLASS =
  'relative h-6 w-full rounded-lg border border-gray-30/80 dark:border-gray-60/80 transition-colors duration-150';
const AXIS_FIELD_WRAPPER_EDITABLE_CLASS = `${AXIS_FIELD_WRAPPER_BASE_CLASS} bg-white/65 hover:bg-white/80 dark:bg-gray-90/65 dark:hover:bg-gray-90/80 focus-within:bg-white dark:focus-within:bg-gray-90 focus-within:border-blue-50`;
const AXIS_FIELD_WRAPPER_READONLY_CLASS = `${AXIS_FIELD_WRAPPER_BASE_CLASS} bg-white/20 dark:bg-gray-90/20 cursor-text`;
const AXIS_FIELD_VALUE_CLASS =
  'block h-[20px] w-full appearance-none border-0 bg-transparent px-1.5 text-right tabular-nums text-primary outline-none';
const AXIS_FIELD_DRAG_CURSOR = 'ew-resize';
const AXIS_FIELD_DRAG_PRECISION = 1000;

function getDragDeltaPerPixel(baseDeltaPerPixel: number, event: MouseEvent): number {
  let deltaPerPixel = baseDeltaPerPixel;
  if (event.ctrlKey) deltaPerPixel *= INSPECTOR_DRAG_SLOW_MODIFIER;
  if (event.shiftKey) deltaPerPixel *= INSPECTOR_DRAG_FAST_MODIFIER;
  return deltaPerPixel;
}

function roundDraggedValue(value: number): number {
  return Math.round(value * AXIS_FIELD_DRAG_PRECISION) / AXIS_FIELD_DRAG_PRECISION;
}

interface AxisFieldDragState {
  lastClientX: number;
  currentValue: number;
  didNotifyDragValueChangeStart: boolean;
  cleanup: () => void;
}

function AxisField({
  axis,
  value,
  editable,
  ariaLabel,
  changed,
  onValueChange,
  dragDeltaPerPixel,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: AxisFieldProps) {
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const valueRef = useRef(value);
  const onValueChangeRef = useRef(onValueChange);
  const onValueChangeStartRef = useRef(onValueChangeStart);
  const onValueChangeEndRef = useRef(onValueChangeEnd);
  const onDragValueChangeStartRef = useRef(onDragValueChangeStart);
  const dragStateRef = useRef<AxisFieldDragState | null>(null);
  const controlTextStyle = changed
    ? {
        ...INSPECTOR_CONTROL_TEXT_STYLE,
        fontWeight: 700,
        // Font files top out at Bold; add a slight stroke so changed values
        // read visibly heavier on small numeric inputs.
        WebkitTextStroke: '0.25px currentColor',
      }
    : INSPECTOR_CONTROL_TEXT_STYLE;
  valueRef.current = value;
  onValueChangeRef.current = onValueChange;
  onValueChangeStartRef.current = onValueChangeStart;
  onValueChangeEndRef.current = onValueChangeEnd;
  onDragValueChangeStartRef.current = onDragValueChangeStart;

  useEffect(
    () => () => {
      dragStateRef.current?.cleanup();
      dragStateRef.current = null;
    },
    [],
  );

  const handleMouseDown = (event: ReactMouseEvent<HTMLInputElement>) => {
    if (event.button !== 1 || dragDeltaPerPixel === undefined) return;
    if (!onValueChangeRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    setDraftValue(null);
    event.currentTarget.blur();
    dragStateRef.current?.cleanup();

    const previousBodyCursor = document.body.style.cursor;
    const previousBodyUserSelect = document.body.style.userSelect;
    document.body.style.cursor = AXIS_FIELD_DRAG_CURSOR;
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      if ((moveEvent.buttons & 4) === 0) {
        dragState.cleanup();
        return;
      }

      const deltaPixels = moveEvent.clientX - dragState.lastClientX;
      if (deltaPixels === 0) return;

      dragState.lastClientX = moveEvent.clientX;
      if (!dragState.didNotifyDragValueChangeStart) {
        dragState.didNotifyDragValueChangeStart = true;
        onDragValueChangeStartRef.current?.();
      }
      dragState.currentValue = roundDraggedValue(
        dragState.currentValue +
          deltaPixels * getDragDeltaPerPixel(dragDeltaPerPixel, moveEvent),
      );
      onValueChangeRef.current?.(dragState.currentValue);
    };

    const handleMouseUp = () => {
      dragStateRef.current?.cleanup();
    };

    const handleWindowBlur = () => {
      dragStateRef.current?.cleanup();
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleWindowBlur);
      document.body.style.cursor = previousBodyCursor;
      document.body.style.userSelect = previousBodyUserSelect;
      onValueChangeEndRef.current?.();
      if (dragStateRef.current?.cleanup === cleanup) {
        dragStateRef.current = null;
      }
    };

    onValueChangeStartRef.current?.();
    dragStateRef.current = {
      lastClientX: event.clientX,
      currentValue: valueRef.current,
      didNotifyDragValueChangeStart: false,
      cleanup,
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleWindowBlur);
  };

  if (!editable) {
    return (
      <div className={AXIS_FIELD_WRAPPER_READONLY_CLASS}>
        <div
          role="textbox"
          aria-readonly="true"
          aria-label={ariaLabel}
          className={`${AXIS_FIELD_VALUE_CLASS} select-text ${
            changed ? 'font-semibold' : ''
          }`}
          style={controlTextStyle}
        >
          {formatTransformValue(value)}
        </div>
        <span
          aria-hidden
          className={`pointer-events-none absolute right-1 bottom-0 left-1 h-[2px] rounded-full ${AXIS_FIELD_STRIP_CLASS[axis]}`}
        />
      </div>
    );
  }

  return (
    <div className={AXIS_FIELD_WRAPPER_EDITABLE_CLASS}>
      <input
        type="text"
        inputMode="decimal"
        value={draftValue ?? formatTransformValue(value)}
        aria-label={ariaLabel}
        className={`${AXIS_FIELD_VALUE_CLASS} ${changed ? 'font-semibold' : ''}`}
        style={controlTextStyle}
        onFocus={() => {
          setDraftValue(formatTransformValue(value));
          onValueChangeStartRef.current?.();
        }}
        onChange={(event) => {
          const nextRaw = event.currentTarget.value;
          setDraftValue(nextRaw);
          const parsed = Number(nextRaw);
          if (Number.isFinite(parsed)) onValueChange?.(parsed);
        }}
        onBlur={() => {
          setDraftValue(null);
          onValueChangeEndRef.current?.();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          setDraftValue(null);
          event.currentTarget.blur();
        }}
        onMouseDown={handleMouseDown}
        onAuxClick={(event) => {
          if (event.button === 1) event.preventDefault();
        }}
      />
      <span
        aria-hidden
        className={`pointer-events-none absolute right-1 bottom-0 left-1 h-[2px] rounded-full ${AXIS_FIELD_STRIP_CLASS[axis]}`}
      />
    </div>
  );
}

export default function AxisTripletFields({
  values,
  editable = false,
  ariaLabelPrefix,
  onAxisChange,
  baselineValues = null,
  changeEpsilon = 5e-4,
  dragDeltaPerPixel,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: AxisTripletFieldsProps) {
  return (
    <div className="grid min-w-0 flex-1 grid-cols-3 gap-1.5">
      {AXES.map((axis, axisIndex) => (
        <AxisField
          key={axis}
          axis={axis}
          value={values[axisIndex]}
          editable={editable}
          ariaLabel={`${ariaLabelPrefix} ${axis.toUpperCase()}`}
          changed={
            baselineValues !== null &&
            axisValueDiffersFromBaseline(
              values[axisIndex],
              baselineValues[axisIndex],
              changeEpsilon,
            )
          }
          onValueChange={(nextValue) => onAxisChange?.(axisIndex, nextValue)}
          dragDeltaPerPixel={dragDeltaPerPixel}
          onValueChangeStart={onValueChangeStart}
          onValueChangeEnd={onValueChangeEnd}
          onDragValueChangeStart={onDragValueChangeStart}
        />
      ))}
    </div>
  );
}
